import { useCallback, useEffect, useMemo, useState } from 'react';

import { addBrandSignifiers } from '../../../../api';
import { useAtom } from 'jotai';
import { useOnboardHSAConfigAtom } from '../../../../atoms';
import { selectedCustomerAtom } from 'atoms/useSelectedCustomerAtom';

// Components
import HSASiginifierInputs, {
  SignifierInputValue,
} from './HSASiginifierInputs';
import OnboardContainer from '../../OnboardContainer';

// Types
import { AddHSASignifiersBody, SignifierLevel } from '../../../../types';
import { isDirtyAtom } from 'atoms/isDirtyAtom';
import useFieldChangeTracker from 'hooks/useFieldChangeTracker';
import { isValidStringValue } from 'utils';

const DEFAULT_VALUE: SignifierInputValue = {
  level: null,
  stringValue: '',
};

// HSA Brand Signifier Form
function HSASignifiersForm() {
  const [hsaConfig, setHSAConfig] = useOnboardHSAConfigAtom();
  const [, setIsDirty] = useAtom(isDirtyAtom);
  const [selectedCustomer, setSelectedCustomer] = useAtom(selectedCustomerAtom);
  const [signifiers, setSignifiers] = useState<SignifierInputValue[]>([
    { ...DEFAULT_VALUE },
  ]);
  const [isValid, setIsValid] = useState(false);
  const [changeCount, handleFieldChange] = useFieldChangeTracker(); // Custom hook increments number to detect input change to trigger mutation - auto save

  // Get previously saved signifiers (Saved Draft or Previous HSA)
  const brandSignifierSavedFormatted = useMemo(() => {
    const brandSignifiersSavedDraft =
      selectedCustomer?.brand_signifiers?.filters ?? [];

    const brandSignifierSavedFormatted: SignifierInputValue[] =
      brandSignifiersSavedDraft.map((f) => ({
        stringValue: f.value || '',
        level: f.level
          ? { label: f.level, value: f.level as SignifierLevel }
          : null,
      }));

    return brandSignifierSavedFormatted;
  }, [selectedCustomer]);

  // Prefill, Update with previous HSA data if available (For the Recalculate & Draft work-flow)
  useEffect(() => {
    // Set signifiers from saved draft if data exists
    if (brandSignifierSavedFormatted.length) {
      setSignifiers(brandSignifierSavedFormatted);
    }
  }, [brandSignifierSavedFormatted]);

  const onChange = useCallback((value: SignifierInputValue, index: number) => {
    setSignifiers((prev) =>
      prev.map((item, i) => (i === index ? value : item))
    );

    const isValid = isValidStringValue(value.stringValue);
    setIsValid(isValid);

    if (!isValid) return;

    // Trigger auto save when input is valid
    handleFieldChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compare all current form fields with last saved data and set isDirty flag to true or false using "changed"
  const changed = useMemo(() => {
    const signifiersChanged =
      brandSignifierSavedFormatted.length !== signifiers.length ||
      !brandSignifierSavedFormatted.every((value, index) => {
        return (
          value.level?.value === signifiers[index].level?.value &&
          value.stringValue === signifiers[index].stringValue
        );
      });

    return signifiersChanged;
  }, [brandSignifierSavedFormatted, signifiers]);

  // Set isDirty flag for Brand Signifiers page if any form value changed since last saved
  useEffect(() => {
    setIsDirty((prev) => ({ ...prev, 'Brand Signifiers': changed }));
  }, [changed, setIsDirty]);

  const onRemove = useCallback((removeIndex: number) => {
    setSignifiers((prev) => prev.filter((_, index) => index !== removeIndex));
    handleFieldChange(); // Triggers auto save with a delay
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAddSignifier = () => {
    setSignifiers((prev) => [...prev, { ...DEFAULT_VALUE }]);
  };

  const onSubmit = async () => {
    if (!hsaConfig?.customer_id) {
      throw new Error('Customer ID is required.');
    }
    if (!hsaConfig?.hsa_id) {
      throw new Error('HSA ID is required.');
    }

    const body: AddHSASignifiersBody = {
      brand_signifiers: {
        filters: signifiers.map(({ level, stringValue }) => {
          if (!level?.value) {
            throw new Error('Level is required.');
          }
          if (!stringValue) {
            throw new Error('String value is required.');
          }
          return {
            level: level.value,
            value: stringValue,
          };
        }),
      },
    };

    const res = await addBrandSignifiers(
      hsaConfig.customer_id,
      hsaConfig.hsa_id,
      body
    );

    setHSAConfig(res.data);
    setSelectedCustomer((prev) => ({
      ...prev,
      brand_signifiers: {
        filters: signifiers.map(({ level, stringValue }) => {
          return {
            level: level?.value ?? '',
            value: stringValue,
          };
        }),
      },
    }));
    return res;
  };

  const isDisabled = useMemo(() => {
    for (const { level, stringValue } of signifiers) {
      if (!level || !stringValue.trim()) return true;
    }
    return false;
  }, [signifiers]);

  // JSX
  return (
    <OnboardContainer
      header="Brand Signifiers"
      onSubmit={onSubmit}
      isDisabled={isDisabled}
      changeCount={changeCount}
      isValidInput={isValid}
    >
      <div className="mt-8 space-y-4">
        {signifiers.map((value, index) => (
          <HSASiginifierInputs
            key={index}
            index={index}
            value={value}
            onChange={onChange}
            onRemove={onRemove}
            isRemoveable={signifiers.length > 1 || index > 0}
          />
        ))}
        <button
          type="button"
          className="border border-solid border-gray400 text-gray400 text-sm px-2 rounded-full flex items-center gap-1"
          onClick={onAddSignifier}
        >
          <span className="text-xl leading-none">+</span>Add Brand Signifier
        </button>
      </div>
    </OnboardContainer>
  );
}

export default HSASignifiersForm;
